/** @format */

/* eslint @typescript-eslint/prefer-nullish-coalescing: "warn" */
import isPropValid from '@emotion/is-prop-valid';
import {
    Button,
    ButtonVariant,
} from '@mpb-com/feather/components/atoms/button';
import { Heading } from '@mpb-com/feather/components/atoms/heading';
import { HeadingSize } from '@mpb-com/feather/components/atoms/heading/types';
import { useMachine } from '@xstate/react';
import { Field, Form, Formik, FormikHelpers } from 'formik';
import { Trans, useTranslation } from 'next-i18next';
import React, { Fragment, FunctionComponent, useContext } from 'react';
import styled from 'styled-components';
import { StateValue } from 'xstate';
import { object, string } from 'yup';

import { FormValues } from '@toucan/client/global/axios';
import { MarketCode, MarketContext } from '@toucan/client/global/market';
import { Model } from '@toucan/client/global/model';
import { StockAlertPageType } from '@toucan/client/global/product';
import { UserContext, legacyEmailRegex } from '@toucan/client/global/user';

import { subscribeToModelAlerts } from '@toucan/client/core/model';

import { Input } from '@toucan/client/shared/components/input';
import { Link } from '@toucan/client/shared/components/link';
import { trackViaDataLayer } from '@toucan/client/shared/helpers/analytics';
import { useCsrf } from '@toucan/client/shared/hooks/use-csrf';

import {
    CreateAlertMachineAction,
    CreateAlertMachineState,
    createAlertMachine,
} from '@toucan/client/blocks/hooks/use-alert/components/create-alert/machine/create-alert.machine';

import { styles } from './create-alert.styles';

export type CreateAlertModel = Partial<Model> &
    Required<Pick<Model, 'id' | 'name'>>;

// Touch and Change
// eslint-disable-next-line @typescript-eslint/consistent-type-definitions
interface CreateAlertFormValues extends FormValues {
    emailAddress: string;
}

// Touch and Change
// eslint-disable-next-line @typescript-eslint/consistent-type-definitions
export interface CreateAlertProps {
    /**
     * The name of the model we are creating an alert for.
     */
    model: CreateAlertModel;

    /**
     * Selection ID for test purposes.
     */
    'data-testid'?: string;

    /**
     * Custom className
     */
    className?: string;

    /**
     * A Handler to close the create alert component
     */
    onClose: (event: React.MouseEvent<HTMLElement>) => void;

    /**
     * The page type that the alert is part of
     */
    pageType?: StockAlertPageType;
}

const StyledCreateAlert = styled('div').withConfig({
    shouldForwardProp: (prop) => isPropValid(prop),
})`
    ${styles}
`;

export const CreateAlert: FunctionComponent<CreateAlertProps> = ({
    model,
    onClose,
    pageType = StockAlertPageType.OTHER,
    ...otherProps
}: CreateAlertProps) => {
    const { t } = useTranslation();
    const { csrfToken } = useCsrf();
    const { activeMarket } = useContext(MarketContext);
    const { user } = useContext(UserContext);
    const [current, send] = useMachine(createAlertMachine);

    async function handleOnSubmit(
        values: CreateAlertFormValues,
        formActions: FormikHelpers<CreateAlertFormValues>
    ) {
        try {
            // disable form submission
            formActions.setSubmitting(true);

            // Call the api to subscribe to user to events
            await subscribeToModelAlerts(
                model.id,
                values.emailAddress,
                activeMarket.code,
                csrfToken
            );

            trackViaDataLayer({
                event: 'STOCK_NOTIFICATION_SIGNUP',
                modelId: model.id,
                pageType,
            });

            send(CreateAlertMachineAction.SUBMIT);
        } catch (error: any) {
            // re enable form submission
            formActions.setSubmitting(false);
            if (error?.validationErrors?.emailAddress) {
                formActions.setErrors(error.validationErrors);
            }
        }
    }

    const formValidationSchema = object().shape({
        emailAddress: string()
            .matches(
                legacyEmailRegex,
                t('Invalid email address', { context: 'create-alert-form' })
            )
            .required(
                t('Please enter an email address', {
                    context: 'create-alert-form',
                })
            ),
    });

    const confirmationModal = (
        <Fragment>
            <Heading data-testid="create-alert_title" size={HeadingSize.H2}>
                {t('Alert created')}
            </Heading>
            <p data-testid="create-alert_description">
                <Trans
                    components={[<strong key="description">$1</strong>]}
                    data-testid="create-alert_description"
                >
                    {t(
                        'MPB will email you as soon as a <0>{{model.name}}</0> is ready to buy. You can cancel these alerts at any time.',
                        {
                            model,
                            interpolation: { escapeValue: false },
                        }
                    )}
                </Trans>
            </p>
            {activeMarket.code === MarketCode.DE_DE && (
                <p data-testid="create-alert_opt-in-message">
                    {`Dankeschön. Bitte klicken Sie auf den Link in der Email, die wir Ihnen geschickt haben, um zu bestätigen, dass Sie Produktalarme für ${model.name} erhalten möchten.`}
                </p>
            )}
        </Fragment>
    );

    const inputModal = (
        <Fragment>
            <Heading data-testid="create-alert_title" size={HeadingSize.H2}>
                {t('Create an alert')}
            </Heading>
            <p data-testid="create-alert_description">
                <Trans components={[<strong key="description">$1</strong>]}>
                    {t(
                        'Get an email as soon as another <0>{{model.name}}</0> is ready to buy. You can cancel these alerts at any time.',
                        {
                            model,
                            interpolation: { escapeValue: false },
                        }
                    )}
                </Trans>
            </p>
            <Formik
                initialValues={{
                    emailAddress: user?.primaryEmail || '',
                }}
                onSubmit={handleOnSubmit}
                validationSchema={formValidationSchema}
            >
                {({ errors, touched, isSubmitting }) => (
                    <Form>
                        <Field name="emailAddress">
                            {({ field }: any) => (
                                <Input
                                    id="emailAddress"
                                    type="text"
                                    className="create-alert-email-input"
                                    data-testid="create-alert_email-input"
                                    label={t('Email address', {
                                        context: 'create-alert-form',
                                    })}
                                    // @ts-ignore - Element implicitly has an any type - Touch and Change
                                    error={errors[field.name]}
                                    // @ts-ignore - Element implicitly has an any type - Touch and Change
                                    touched={touched[field.name]}
                                    {...field}
                                />
                            )}
                        </Field>

                        <Button
                            variant={ButtonVariant.PRIMARY}
                            type="submit"
                            isLoading={isSubmitting}
                            className="create-alert-submit-button"
                            data-testid="create-alert_submit-button"
                        >
                            {t('Create Alert')}
                        </Button>
                        <Link
                            className="create-alert-cancel-button"
                            data-testid="create-alert_cancel-button"
                            onClick={onClose}
                        >
                            {t('Cancel')}
                        </Link>
                    </Form>
                )}
            </Formik>
        </Fragment>
    );

    function view(state: StateValue) {
        switch (state) {
            case CreateAlertMachineState.CONFIRMATION:
                return confirmationModal;
            case CreateAlertMachineState.FORM:
            default:
                return inputModal;
        }
    }

    return (
        <StyledCreateAlert model={model} onClose={onClose} {...otherProps}>
            {view(current.value)}
        </StyledCreateAlert>
    );
};
